import {faMagnifyingGlass} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {SearchResultItem} from '@rdcomponents/navigation/quick-search/search-result-item';
import {Empty, Input, type InputRef, Modal} from 'antd';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useLocale} from '../../../provider/locale/locale-hook';
import {Locale} from '../../../provider/locale/localization';
import {useUrl} from '../../../util/url-hook/url-hook';

import {addRecentVisit, getRecentVisits} from './recent-visits-storage';
import type {SearchableMenuItemType} from './use-searchable-menu-items';
import {useSearchableMenuItems} from './use-searchable-menu-items';
import * as styles from './quick-search-modal.scss';

type PropsType = {
    open: boolean;
    onClose: () => void;
};

// eslint-disable-next-line max-statements
export function QuickSearchModal(props: PropsType): JSX.Element {
    const {open, onClose} = props;
    const [query, setQuery] = useState('');
    const {pushUrl} = useUrl();
    const {getLocalizedString} = useLocale();
    const searchableItems = useSearchableMenuItems();
    const recentVisits = useMemo(
        () => getRecentVisits(),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when modal opens
        [open]
    );

    const recentItems = useMemo(() => {
        const pathToItem = new Map<string, SearchableMenuItemType>();

        searchableItems.forEach((item) => {
            pathToItem.set(item.path, item);

            if (item.navigatePath !== item.path) {
                pathToItem.set(item.navigatePath, item);
            }
        });

        return recentVisits.map((visit) => pathToItem.get(visit.path)).filter(Boolean) as Array<SearchableMenuItemType>;
    }, [recentVisits, searchableItems]);

    const filteredItems = useMemo(() => {
        const trimmed = query.trim().toLowerCase();

        if (!trimmed) {
            return [];
        }

        return searchableItems.filter((item) => item.displayLabel.toLowerCase().includes(trimmed));
    }, [query, searchableItems]);

    const isEmptySearch = !query.trim();
    const showRecent = isEmptySearch && recentItems.length > 0;
    const showAllItems = isEmptySearch && searchableItems.length > 0;
    const showFilteredResults = !isEmptySearch && filteredItems.length > 0;
    const showEmpty = !isEmptySearch && filteredItems.length === 0;

    const flatItems = useMemo(() => {
        if (!isEmptySearch) {
            return filteredItems;
        }

        const recentPaths = new Set(recentItems.map((index) => index.path));
        const restItems = searchableItems.filter((item) => !recentPaths.has(item.path));

        return [...recentItems, ...restItems];
    }, [isEmptySearch, recentItems, searchableItems, filteredItems]);

    const [selectedIndex, setSelectedIndex] = useState(0);
    const searchInputRef = useRef<InputRef | null>(null);
    const itemReferences = useRef<Map<number, HTMLButtonElement | null>>(new Map());
    const selectedIndexRef = useRef(selectedIndex);

    selectedIndexRef.current = selectedIndex;

    useEffect(() => {
        setSelectedIndex(0);
    }, [query, flatItems.length]);

    useEffect(() => {
        const element = itemReferences.current.get(selectedIndex);

        element?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }, [selectedIndex]);

    const handleItemClick = useCallback(
        (item: SearchableMenuItemType) => {
            addRecentVisit(item.navigatePath);

            if (item.onSelect) {
                item.onSelect();
                onClose();

                return;
            }

            pushUrl(item.navigatePath);
            onClose();
        },
        [onClose, pushUrl]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (flatItems.length === 0) {
                return;
            }

            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    setSelectedIndex((previous) => (previous + 1) % flatItems.length);

                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    setSelectedIndex((previous) => (previous - 1 + flatItems.length) % flatItems.length);

                    break;
                }
                case 'Enter': {
                    event.preventDefault();
                    const item = flatItems[selectedIndexRef.current];

                    if (item) {
                        handleItemClick(item);
                    }

                    break;
                }
                // No default
            }
        },
        [flatItems, handleItemClick]
    );

    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, handleKeyDown]);

    return (
        <Modal
            afterOpenChange={(openState: boolean) => {
                if (openState) {
                    setQuery('');
                    // Modal moves focus to the close button after mount; defer past focus trap.
                    window.setTimeout(() => {
                        searchInputRef.current?.focus({preventScroll: true});
                    }, 0);
                }
            }}
            closable
            footer={null}
            onCancel={onClose}
            open={open}
            title={<Locale stringKey="NAVIGATION__QUICK_SEARCH__TITLE" />}
        >
            <Input
                allowClear
                className={styles.QuickSearchModal_input}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={getLocalizedString('NAVIGATION__QUICK_SEARCH__INPUT_PLACEHOLDER')}
                prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                ref={searchInputRef}
                value={query}
            />
            <div className={styles.QuickSearchModal_content}>
                {showRecent && (
                    <div className={styles.QuickSearchModal_section}>
                        <div className={styles.QuickSearchModal_sectionTitle}>
                            <Locale stringKey="NAVIGATION__QUICK_SEARCH__RECENT_VISITS" />
                        </div>
                        <ul className={styles.QuickSearchModal_list}>
                            {recentItems.map((item, index) => (
                                <SearchResultItem
                                    isSelected={selectedIndex === index}
                                    item={item}
                                    key={item.path}
                                    onClick={handleItemClick}
                                    onRef={(element) => itemReferences.current.set(index, element)}
                                />
                            ))}
                        </ul>
                    </div>
                )}
                {showAllItems && (
                    <div className={styles.QuickSearchModal_section}>
                        <div className={styles.QuickSearchModal_sectionTitle}>
                            <Locale stringKey="NAVIGATION__QUICK_SEARCH__ALL_SECTIONS" />
                        </div>
                        <ul className={styles.QuickSearchModal_list}>
                            {searchableItems
                                .filter((item) => !recentItems.some((recentItem) => recentItem.path === item.path))
                                .map((item, index) => {
                                    const globalIndex = recentItems.length + index;

                                    return (
                                        <SearchResultItem
                                            isSelected={selectedIndex === globalIndex}
                                            item={item}
                                            key={item.path}
                                            onClick={handleItemClick}
                                            onRef={(element) => itemReferences.current.set(globalIndex, element)}
                                        />
                                    );
                                })}
                        </ul>
                    </div>
                )}
                {showFilteredResults && (
                    <ul className={styles.QuickSearchModal_list}>
                        {filteredItems.map((item, index) => (
                            <SearchResultItem
                                isSelected={selectedIndex === index}
                                item={item}
                                key={item.path}
                                onClick={handleItemClick}
                                onRef={(element) => itemReferences.current.set(index, element)}
                            />
                        ))}
                    </ul>
                )}
                {showEmpty && <Empty description={<Locale stringKey="NAVIGATION__QUICK_SEARCH__NOTHING_FOUND" />} />}
            </div>
        </Modal>
    );
}
